/**
 * Sample CSV templates for the Bank feed importer. Each one mirrors
 * the real shape of that bank's statement export — including the
 * Shift_JIS-decoded Japanese column headers for Mizuho/MUFG — so a user
 * can download → fill in → upload without guessing the format.
 *
 * The actual backend decoders + column-mappers live in
 * connector-jp-banks.service.ts. Keep this in sync with `normalize()`
 * over there if the bank's column layout changes.
 */

import type { JpBank } from '../hooks/useBankFeed';

export interface BankTemplate {
  filename: string;
  mime: string;
  body: string;
}

export const BANK_TEMPLATES: Record<JpBank, BankTemplate> = {
  mizuho: {
    filename: 'mizuho-statement-sample.csv',
    mime: 'text/csv;charset=shift_jis',
    body: [
      '取引日,お引出し,お預入れ,お取り扱い内容,残高',
      '2026/04/05,12340,,カ）サクラコウコク,1234567',
      '2026/04/12,,250000,ニュウキン カブシキガイシャ クライアントエー,1484567',
      '2026/04/20,8500,,デンキ ガス スイドウ,1476067',
    ].join('\n'),
  },
  smbc: {
    filename: 'smbc-statement-sample.csv',
    mime: 'text/csv;charset=utf-8',
    body: [
      '年月日,お引出し,お預入れ,お取り扱い内容,残高',
      '2026-04-06,3200,,カ）モバイルキャリア,3210000',
      '2026-04-15,,180000,カ）クライアントビー,3390000',
    ].join('\n'),
  },
  mufg: {
    filename: 'mufg-statement-sample.csv',
    mime: 'text/csv;charset=shift_jis',
    body: [
      '日付,摘要,お支払金額,お預り金額,差引残高',
      '2026/04/07,カ）クラウド ベンダー,12000,,890000',
      '2026/04/22,カ）コンサルティング,,350000,1240000',
    ].join('\n'),
  },
  rakuten: {
    filename: 'rakuten-statement-sample.csv',
    mime: 'text/csv;charset=utf-8',
    body: [
      'date,description,withdrawal,deposit,balance',
      '2026-04-09,AWS JAPAN GK,124500,,5670000',
      '2026-04-18,CLIENT C K.K.,,420000,6090000',
    ].join('\n'),
  },
  paypay: {
    filename: 'paypay-statement-sample.csv',
    mime: 'text/csv;charset=utf-8',
    body: [
      'date,description,amount,balance',
      '2026-04-10,Stripe Payouts,890000,8900000',
      '2026-04-25,GMO Pepabo,-12500,8887500',
    ].join('\n'),
  },
  universal: {
    filename: 'universal-statement-sample.csv',
    mime: 'text/csv;charset=utf-8',
    body: [
      'posted_on,amount,description,counterparty',
      '2026-04-05,-12340,Sakura Advertising invoice,株式会社 サクラ広告',
      '2026-04-12,250000,Client A April retainer,Client A K.K.',
      '2026-04-20,-8500,TEPCO power,東京電力エナジーパートナー',
    ].join('\n'),
  },
};

export function downloadBankTemplate(bank: JpBank): void {
  const tpl = BANK_TEMPLATES[bank];
  const blob = new Blob([tpl.body], { type: tpl.mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = tpl.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
